import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  LinearProgress,
  Divider,
  List,
  ListItem,
  IconButton,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddSourcePopup from "./AddAnotherSource";
import { Close, Description } from "@mui/icons-material";
import { uploadDocumentToTransformer } from "../../../../services/webServices";
import MessagePopup from "../../../popup-components/MessagePopup";
import SessionExpired from "../../../popup-components/SessionExpiredPopup";

const WebsiteAnalysis = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [urls, setUrls] = useState(["https://"]); // multiple URLs support
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const handleUrlChange = (index, e) => {
    const input = e.target.value;
    let fullUrl = input.startsWith("https://")
      ? input
      : `https://${input.replace(/^https?:\/\//, "")}`;

    const updatedUrls = [...urls];
    updatedUrls[index] = fullUrl;
    setUrls(updatedUrls);
  };

  const removeUrl = (index) => {
    if (urls.length > 1) {
      setUrls((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const hasValidUrls = urls.some((u) => u && u !== "https://");
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };
  // API call for scanning the given URLs
  const handleAnalyze = async () => {
    const validUrls = urls
      .map((u) => u.trim())
      .filter((u) => u && u !== "https://");

    if (validUrls.length === 0 && uploadedFiles.length === 0) {
      console.warn("No URLs or files to analyze");
      return;
    }

    try {
      setIsAnalyzing(true);

      const response = await uploadDocumentToTransformer(
        uploadedFiles,
        validUrls
      );

      navigate("/dashboard/qe-strategy", {
        state: { outputData: response.data.structuredCSV || response.data },
      });
    } catch (error) {
      console.error("Error analyzing:", error);
      if (error.response?.data?.message === "Your session has expired.") {
        setSessionExpired(true);
        setIsErrorPopupOpen(false);
      } else {
        // Show popup for all other errors
        const msg =
          error?.response?.data?.error ||
          error?.message ||
          "An unexpected error occurred. Please try again.";
        setErrorMessage(msg);
        setIsErrorPopupOpen(true);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: { xs: "95%", sm: "90%", md: "85%", lg: 1000, xl: 1200 },
          maxWidth: "1400px",
          backgroundColor: "#fff",
          boxShadow: "0 0 0 1px #f0f0f0",
          p: { xs: 2, sm: 3, md: 4 },
          mt: 3,
          mb: 2,
          mx: "auto",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.3rem" },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          Website Analysis
        </Typography>

        <Box
          sx={{
            border: "2px dashed #99c5fa",
            backgroundColor: "#e9f3ff",
            borderRadius: 2,
            p: { xs: 2, sm: 3, md: 4 },
            textAlign: "center",
            position: "relative",
            mb: 3,
          }}
        >
          {/* Multiple URL fields */}
          {urls.map((url, index) => (
            <Box
              key={index}
              sx={{
                mb: { xs: 1.5, sm: 2 },
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <TextField
                fullWidth
                value={url.replace(/^https?:\/\//, "")}
                onChange={(e) => handleUrlChange(index, e)}
                placeholder="www.qmentisai.ai"
                size={window.innerWidth < 600 ? "small" : "medium"}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            color: "gray",
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                        >
                          https://
                        </Typography>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {urls.length > 1 && (
                <IconButton
                  onClick={() => removeUrl(index)}
                  sx={{
                    color: "error.main",
                    "&:hover": {
                      backgroundColor: "rgba(211, 47, 47, 0.06)",
                    },
                  }}
                >
                  <Close sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              )}
            </Box>
          ))}

          {isAnalyzing && hasValidUrls && (
            <Box sx={{ px: 1 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary", mb: 1 }}
              >
                Generating Insights...
              </Typography>
              <LinearProgress
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#eaf4fc",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    backgroundColor: "#329efc",
                  },
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "flex-end",
              mt: { xs: 2, sm: 2 },
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setUrls([...urls, "https://"])}
              sx={{
                textTransform: "none",
                borderColor: "#0094FB",
                color: "#0094FB",
                height: { xs: 38, sm: 42 },
                width: { xs: "100%", sm: "auto" },
                borderRadius: 2,
                fontSize: { xs: "0.875rem", sm: "1rem" },
                minWidth: { sm: 120 },
              }}
            >
              + Add URL
            </Button>
            <Button
              variant="contained"
              onClick={handleAnalyze}
              sx={{
                backgroundColor: "#3898fc",
                fontWeight: 100,
                height: { xs: 38, sm: 40 },
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 140 },
                borderRadius: 2,
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              disabled={isAnalyzing || urls.every((u) => u === "https://")}
            >
              {isAnalyzing ? "Analyzing..." : "Initiate Analysis"}
            </Button>
          </Box>
        </Box>
        {isErrorPopupOpen && (
          <MessagePopup
            modalOpen={isErrorPopupOpen}
            okayHandler={() => setIsErrorPopupOpen(false)}
            message={errorMessage}
          />
        )}

        {sessionExpired && <SessionExpired />}
      </Box>
      {/*  Uploaded Files Section (Box 2) */}
      {uploadedFiles.length > 0 && (
        <Box
          sx={{
            width: { xs: "95%", sm: "90%", md: "85%", lg: 1000, xl: 1200 },
            maxWidth: "1400px",
            backgroundColor: "#fff",
            borderRadius: 2,
            boxShadow: "0 0 0 1px #f0f0f0",
            p: { xs: 2, sm: 3, md: 4 },
            mt: 2,
            mb: 2,
            mx: "auto",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              mt: -2,
              mb: { xs: 1.5, sm: 2 },
              fontWeight: 600,
              textAlign: { xs: "center", sm: "left" },
              fontSize: { xs: "1rem", sm: "1.1rem" },
            }}
          >
            Uploaded Files
          </Typography>
          <List sx={{ p: 0 }}>
            {uploadedFiles.map((file, index) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => removeFile(index)}
                      sx={{
                        color: "error.main",
                        "&:hover": {
                          backgroundColor: "rgba(211, 47, 47, 0.06)",
                        },
                      }}
                    >
                      <Close sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                    <Description
                      sx={{
                        color: "#1495f3",
                        fontSize: { xs: 22, sm: 26 },
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: 14, sm: 16 },
                        }}
                      >
                        {file.name}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                      >
                        {formatFileSize(file.size)} • 1 min ago
                      </Typography>
                    }
                  />
                </ListItem>

                <Divider />
              </React.Fragment>
            ))}
          </List>

          {isAnalyzing && (
            <>
              <Divider sx={{ mt: 0 / 5, mb: 2 }} />
              <Box sx={{ px: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: "text.secondary", mb: 1 }}
                >
                  Analyzing file...
                </Typography>
                <LinearProgress
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "#eaf4fc",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 3,
                      backgroundColor: "#329efc",
                    },
                  }}
                />
              </Box>
            </>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: { xs: 2, sm: 3 },
            }}
          >
            {/* Analyze Files Button */}
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#329efc",
                height: { xs: 38, sm: 40 },
                width: { xs: "100%", sm: 160 },
                minWidth: { sm: 120 },
                borderRadius: 1,
                textTransform: "none",
                fontSize: { xs: "0.875rem", sm: "1rem" },
                "&:hover": { backgroundColor: "#2887e2" },
              }}
              disabled={isAnalyzing || uploadedFiles.length === 0}
              endIcon={
                <ArrowForwardIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              }
              onClick={handleAnalyze}
            >
              {isAnalyzing ? "Analyzing..." : "Analyze Files"}
            </Button>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          width: { xs: "95%", sm: "90%", md: "85%", lg: 1000, xl: 1200 },
          maxWidth: "1400px",
          mx: "auto",
          mt: 3,
          mb: 3,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "flex-end",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
            onClick={() => setOpen(true)}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              borderColor: "#0094FB",
              borderWidth: 1.5,
              color: "#0094FB",
              fontSize: { xs: "0.825rem", sm: "0.9rem" },
              fontWeight: 600,
              height: { xs: 34, sm: 36 },
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: 160 },
              "&:hover": {
                borderColor: "#0094FB",
                backgroundColor: "rgba(0,148,251,0.04)",
              },
            }}
          >
            Add another source
          </Button>

          <Button
            variant="contained"
            sx={{
              borderRadius: 2,
              backgroundColor: "#0094FB",
              textTransform: "none",
              width: { xs: "100%", sm: 100 },
              height: { xs: 34, sm: 36 },
              fontSize: { xs: "0.825rem", sm: "1rem" },
            }}
            endIcon={<ArrowForwardIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* Popup Drawer */}
      <AddSourcePopup
        open={open}
        onClose={() => setOpen(false)}
        onFileUpload={(files) =>
          setUploadedFiles((prev) => [...prev, ...files])
        }
      />
    </>
  );
};

export default WebsiteAnalysis;
